import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  defaultMapDiscoverFilters,
  getMapDatePresetRange,
  MAP_DATE_PRESET_IDS,
  mapDiscoverFiltersActive,
} from '../../utils/discoverEventFilters';
import './EventFiltersPopup.css';

function EventFiltersPopup({ onClose, initialFilters = {}, onApply }) {
  const { t } = useTranslation(['events', 'discover']);

  const [filters, setFilters] = useState(() => ({
    ...defaultMapDiscoverFilters(),
    ...initialFilters,
  }));

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleClearFilters = () => {
    const clearedFilters = defaultMapDiscoverFilters();
    setFilters(clearedFilters);
    if (onApply) {
      onApply(clearedFilters);
    }
    if (onClose) {
      onClose();
    }
  };

  const handleApply = () => {
    if (onApply) {
      onApply(filters);
    }
    if (onClose) {
      onClose();
    }
  };

  const hasActiveFilters = () => {
    return mapDiscoverFiltersActive(filters);
  };

  // Get today's date in YYYY-MM-DD format for min date
  const today = new Date().toISOString().split('T')[0];

  const handleDatePreset = (preset) => {
    setFilters((prev) => {
      const nextPreset = prev.datePreset === preset ? '' : preset;
      if (!nextPreset) {
        return { ...prev, datePreset: '', dateFrom: '', dateTo: '' };
      }
      const range = getMapDatePresetRange(nextPreset);
      return {
        ...prev,
        datePreset: nextPreset,
        dateFrom: range?.from || '',
        dateTo: range?.to || '',
      };
    });
  };

  const handleCustomDate = () => {
    setFilters((prev) => {
      if (prev.datePreset === 'custom') {
        return { ...prev, datePreset: '', dateFrom: '', dateTo: '' };
      }
      return { ...prev, datePreset: 'custom', dateFrom: '', dateTo: '' };
    });
  };

  return (
    <div className="event-filters-popup">
      <div className="event-filters-content">
        <div className="filter-group">
          <label className="filter-label">
            {t('discover.map.datePreset.groupLabel', 'Szybki zakres dat')}
          </label>
          <div className="filter-presets-group">
            {MAP_DATE_PRESET_IDS.map((preset) => (
              <button
                key={preset}
                type="button"
                className={`filter-preset-btn ${filters.datePreset === preset ? 'active' : ''}`}
                onClick={() => handleDatePreset(preset)}
              >
                {t(`discover.map.datePreset.${preset}`)}
              </button>
            ))}
            <button
              type="button"
              className={`filter-preset-btn ${filters.datePreset === 'custom' ? 'active' : ''}`}
              onClick={handleCustomDate}
            >
              {t('discover.map.datePreset.custom', 'Własne')}
            </button>
          </div>
        </div>

        {/* Date Range */}
        {filters.datePreset === 'custom' && (
          <>
            <div className="filter-group">
              <label className="filter-label">
                {t('events.filters.dateFrom', 'Data od')}
              </label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                min={today}
                className="filter-input"
              />
            </div>

            <div className="filter-group">
              <label className="filter-label">
                {t('events.filters.dateTo', 'Data do')}
              </label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                min={filters.dateFrom || today}
                className="filter-input"
              />
            </div>
          </>
        )}

        {/* Free/Paid */}
        <div className="filter-group">
          <label className="filter-label">
            {t('events.filters.price', 'Cena')}
          </label>
          <div className="filter-radio-group">
            <label className="filter-radio-label">
              <input
                type="radio"
                name="isFree"
                value=""
                checked={filters.isFree === null}
                onChange={() => handleFilterChange('isFree', null)}
              />
              <span>{t('events.filters.priceAll', 'Wszystkie')}</span>
            </label>
            <label className="filter-radio-label">
              <input
                type="radio"
                name="isFree"
                value="true"
                checked={filters.isFree === true}
                onChange={() => handleFilterChange('isFree', true)}
              />
              <span>{t('events.filters.priceFree', 'Bezpłatne')}</span>
            </label>
            <label className="filter-radio-label">
              <input
                type="radio"
                name="isFree"
                value="false"
                checked={filters.isFree === false}
                onChange={() => handleFilterChange('isFree', false)}
              />
              <span>{t('events.filters.pricePaid', 'Płatne')}</span>
            </label>
          </div>
        </div>

        <div className="filter-group">
          <label className="filter-label">
            {t('discover.map.pricePerPersonPln', 'Cena (zł / osoba)')}
          </label>
          <div className="filter-range-group">
            <input
              type="number"
              min="0"
              step="0.01"
              placeholder={t('discover.map.priceMin', 'Od')}
              value={filters.priceMin}
              onChange={(e) => handleFilterChange('priceMin', e.target.value)}
              className="filter-input filter-input-small"
            />
            <span className="filter-range-separator">-</span>
            <input
              type="number"
              min="0"
              step="0.01"
              placeholder={t('discover.map.priceMax', 'Do')}
              value={filters.priceMax}
              onChange={(e) => handleFilterChange('priceMax', e.target.value)}
              className="filter-input filter-input-small"
            />
          </div>
        </div>

        {/* Participants Range */}
        <div className="filter-group">
          <label className="filter-label">
            {t('events.filters.participants', 'Liczba uczestników')}
          </label>
          <div className="filter-range-group">
            <input
              type="number"
              min="1"
              max="20"
              placeholder={t('events.filters.participantsMin', 'Min')}
              value={filters.minParticipants}
              onChange={(e) => handleFilterChange('minParticipants', e.target.value)}
              className="filter-input filter-input-small"
            />
            <span className="filter-range-separator">-</span>
            <input
              type="number"
              min="1"
              max="20"
              placeholder={t('events.filters.participantsMax', 'Max')}
              value={filters.maxParticipants}
              onChange={(e) => handleFilterChange('maxParticipants', e.target.value)}
              className="filter-input filter-input-small"
            />
          </div>
        </div>

        <div className="filter-group">
          <label className="filter-check-label">
            <input
              type="checkbox"
              checked={Boolean(filters.friendsOnly)}
              onChange={(e) => handleFilterChange('friendsOnly', e.target.checked)}
            />
            <span>{t('discover.map.friendsEvents', 'Wydarzenia znajomych (organizator)')}</span>
          </label>
          <label className="filter-check-label">
            <input
              type="checkbox"
              checked={Boolean(filters.mineOnly)}
              onChange={(e) => handleFilterChange('mineOnly', e.target.checked)}
            />
            <span>{t('discover.map.myEvents', 'Moje wydarzenia')}</span>
          </label>
        </div>
      </div>

      <div className="event-filters-actions">
        {hasActiveFilters() && (
          <button
            type="button"
            className="filter-btn filter-btn-clear"
            onClick={handleClearFilters}
          >
            {t('events.filters.clear', 'Wyczyść')}
          </button>
        )}
        <div className="filter-actions-right">
          <button
            type="button"
            className="filter-btn filter-btn-cancel"
            onClick={onClose}
          >
            {t('events.filters.cancel', 'Anuluj')}
          </button>
          <button
            type="button"
            className="filter-btn filter-btn-apply"
            onClick={handleApply}
          >
            {t('events.filters.apply', 'Zastosuj')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default EventFiltersPopup;
